import type { RosterManageEntry, RosterResponse, RosterSection } from '@clan-heart/shared';
import { rosterSectionSchema } from '@clan-heart/shared';

/** Fixed display order for every section-grouped list on the Crew page. */
export const SECTION_ORDER = rosterSectionSchema.options;

export const SECTION_LABELS: Record<RosterSection, string> = {
  officers: 'Officers',
  members: 'Members',
  honorable: 'Honorable Mentions',
  dockmates: 'Respected Dockmates',
  feared: 'Feared Sailors',
};

/**
 * Entries whose name contains `query`, case-insensitively, after trimming
 * both sides. An empty (or all-whitespace) query matches everything.
 */
export function filterEntries<T extends { name: string }>(entries: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (q === '') return entries;
  return entries.filter((entry) => entry.name.toLowerCase().includes(q));
}

/**
 * Groups entries by roster section, each group sorted by `position`
 * ascending. The returned object's keys are always inserted in
 * `SECTION_ORDER` (Officers, Members, Honorable Mentions, Respected
 * Dockmates, Feared Sailors), regardless of the input's order.
 */
export function groupBySection<T extends { section: RosterSection; position: number }>(
  entries: T[],
): Record<RosterSection, T[]> {
  const grouped: Record<RosterSection, T[]> = {
    officers: [],
    members: [],
    honorable: [],
    dockmates: [],
    feared: [],
  };
  for (const entry of entries) grouped[entry.section].push(entry);
  for (const section of SECTION_ORDER) grouped[section].sort((a, b) => a.position - b.position);
  return grouped;
}

/** Whether a roster/manage entry is linked to a Discord account. */
export function isLinked(entry: RosterManageEntry): boolean {
  return entry.discordUserId !== null;
}

/**
 * `/crew/<slug>` when the entry belongs to a member with a profile, else null.
 * Only those get the compact row: their editing lives in the profile's
 * Officer controls. Everyone else — unlinked Hall entries, but also entries
 * linked to an account that isn't approved yet or never signed in (e.g. a
 * mistyped Discord id) — keeps the full inline edit/remove row.
 */
export function profileHref(entry: Pick<RosterManageEntry, 'profileSlug'>): string | null {
  return entry.profileSlug ? `/crew/${entry.profileSlug}` : null;
}
